import { Box } from '@mui/material';
import { Section } from '@qwickapps/react-framework';
import React, { useEffect, useState } from 'react';

const DownloadsPage: React.FC = () => {
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);
  const [latestVersion, setLatestVersion] = useState<string>('0.1.1');
  const [detectedOS, setDetectedOS] = useState<'Windows' | 'macOS' | 'Linux' | 'Unknown'>('Unknown');

  useEffect(() => {
    // Detect user's operating system
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('win') !== -1) {
      setDetectedOS('Windows');
    } else if (userAgent.indexOf('mac') !== -1) {
      setDetectedOS('macOS');
    } else if (userAgent.indexOf('linux') !== -1) {
      setDetectedOS('Linux');
    }

    // Fetch latest version from qwickforge.com
    fetch('https://qwickforge.com/downloads/latest-version.txt')
      .then(res => {
        if (!res.ok || res.headers.get('content-type')?.includes('text/html')) {
          throw new Error('Version file not found');
        }
        return res.text();
      })
      .then(text => {
        const version = text.split('\n')[0].trim();
        // Validate version format (should be like "0.1.0", not HTML)
        if (version && /^\d+\.\d+\.\d+/.test(version)) {
          setLatestVersion(version);
        }
      })
      .catch(() => {
        // Silently use default version in development
        console.debug('Using default version (latest-version.txt not available)');
      });
  }, []);

  const handleCopyCommand = (command: string, id: string) => {
    navigator.clipboard.writeText(command);
    setCopiedCommand(id);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  const installCommand = `curl -fsSL https://qwickforge.com/downloads/install.sh | QWICKFORGE_VERSION=${latestVersion} bash`;
  const windowsInstallCommand = `Set-ExecutionPolicy Bypass -Scope Process -Force; $env:QWICKFORGE_VERSION="${latestVersion}"; iex ((New-Object System.Net.WebClient).DownloadString('https://qwickforge.com/downloads/install.ps1'))`;

  return (
    <Box>
      <Section padding="large">
        {/* Download Section */}
        <Box style={{ marginBottom: '3rem' }}>
          <h2 style={{ color: 'var(--theme-text-primary)', marginBottom: '1rem' }}>
            Download QwickForge Bundle
          </h2>
          {detectedOS !== 'Unknown' && (
            <p style={{ color: 'var(--theme-text-secondary)', marginBottom: '1.5rem' }}>
              Detected OS: <span style={{
                backgroundColor: 'var(--theme-primary)',
                color: 'var(--theme-on-primary)',
                padding: '0.25rem 0.5rem',
                borderRadius: 'var(--theme-border-radius-small)',
                fontSize: '0.875rem'
              }}>{detectedOS}</span>
            </p>
          )}

          <p style={{ color: 'var(--theme-text-secondary)', marginBottom: '1.5rem' }}>
            Latest Version: <span style={{
              backgroundColor: 'var(--theme-primary)',
              color: 'var(--theme-on-primary)',
              padding: '0.25rem 0.5rem',
              borderRadius: 'var(--theme-border-radius-small)',
              fontSize: '0.875rem'
            }}>v{latestVersion}</span>
          </p>

          {/* Direct Download Card */}
          <Box style={{
            border: '1px solid var(--theme-border-main)',
            borderRadius: 'var(--theme-border-radius)',
            padding: '2rem',
            backgroundColor: 'var(--theme-surface)',
            marginBottom: '2rem'
          }}>
            <h3 style={{ color: 'var(--theme-text-primary)', marginBottom: '1rem', fontSize: '1.25rem' }}>
              QwickForge Complete Bundle
            </h3>
            <p style={{ color: 'var(--theme-text-secondary)', marginBottom: '1.5rem' }}>
              Includes forge-service, middleware-client, and CLI tools for Windows, macOS, and Linux
            </p>
            <Box style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <span style={{
                padding: '0.25rem 0.75rem',
                backgroundColor: 'var(--theme-primary-light)',
                color: 'var(--theme-on-primary)',
                borderRadius: 'var(--theme-border-radius-small)',
                fontSize: '0.875rem'
              }}>Windows</span>
              <span style={{
                padding: '0.25rem 0.75rem',
                backgroundColor: 'var(--theme-primary-light)',
                color: 'var(--theme-on-primary)',
                borderRadius: 'var(--theme-border-radius-small)',
                fontSize: '0.875rem'
              }}>macOS</span>
              <span style={{
                padding: '0.25rem 0.75rem',
                backgroundColor: 'var(--theme-primary-light)',
                color: 'var(--theme-on-primary)',
                borderRadius: 'var(--theme-border-radius-small)',
                fontSize: '0.875rem'
              }}>Linux</span>
            </Box>
            <a
              href={`https://qwickforge.com/downloads/v${latestVersion}/qwickforge-bundle.tar.gz`}
              download
              style={{
                display: 'inline-block',
                padding: '0.875rem 2rem',
                backgroundColor: 'var(--theme-primary)',
                color: 'var(--theme-on-primary)',
                textDecoration: 'none',
                borderRadius: 'var(--theme-border-radius)',
                fontSize: '1rem',
                fontWeight: '600',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-primary-dark)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-primary)';
              }}
            >
              Download Bundle (~50MB)
            </a>
          </Box>

          {/* Install Instructions - Windows */}
          {detectedOS === 'Windows' && (
            <Box style={{
              padding: '1.5rem',
              backgroundColor: 'var(--theme-surface-variant)',
              border: '1px solid var(--theme-border-main)',
              borderRadius: 'var(--theme-border-radius)',
              marginBottom: '1rem'
            }}>
              <h3 style={{ color: 'var(--theme-text-primary)', marginBottom: '0.75rem', fontSize: '1.125rem' }}>
                Quick Install (Windows PowerShell)
              </h3>
              <Box style={{
                padding: '0.75rem',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: 'var(--theme-border-radius-small)',
                marginBottom: '1rem'
              }}>
                <p style={{
                  margin: 0,
                  color: '#856404',
                  fontSize: '0.875rem',
                  lineHeight: '1.5'
                }}>
                  ⚠️ <strong>Note:</strong> The installation script is currently not signed. The command below temporarily bypasses execution policy for this process only.
                </p>
              </Box>
              <Box style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: 'var(--theme-code-bg)',
                color: 'var(--theme-code-text)',
                padding: '1rem',
                borderRadius: 'var(--theme-border-radius-small)',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                overflowX: 'auto'
              }}>
                <code style={{ flex: 1 }}>
                  {windowsInstallCommand}
                </code>
                <button
                  onClick={() => handleCopyCommand(windowsInstallCommand, 'windows')}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: '#ffffff',
                    padding: '0.5rem',
                    borderRadius: 'var(--theme-border-radius-small)',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                  title={copiedCommand === 'windows' ? 'Copied!' : 'Copy command'}
                >
                  {copiedCommand === 'windows' ? 'Copied' : 'Copy'}
                </button>
              </Box>
              <p style={{
                marginTop: '1rem',
                color: 'var(--theme-text-secondary)',
                fontSize: '0.875rem'
              }}>
                Requires: <strong>PowerShell 5.1+</strong>, <strong>Node.js 18+</strong>, <strong>npm 8+</strong>
              </p>
            </Box>
          )}

          {/* Install Instructions - macOS/Linux */}
          {detectedOS !== 'Windows' && detectedOS !== 'Unknown' && (
            <Box style={{
              padding: '1.5rem',
              backgroundColor: 'var(--theme-surface-variant)',
              border: '1px solid var(--theme-border-main)',
              borderRadius: 'var(--theme-border-radius)'
            }}>
              <h3 style={{ color: 'var(--theme-text-primary)', marginBottom: '0.75rem', fontSize: '1.125rem' }}>
                Quick Install (macOS / Linux)
              </h3>
              <Box style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: 'var(--theme-code-bg)',
                color: 'var(--theme-code-text)',
                padding: '1rem',
                borderRadius: 'var(--theme-border-radius-small)',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                overflowX: 'auto'
              }}>
                <code style={{ flex: 1 }}>
                  {installCommand}
                </code>
                <button
                  onClick={() => handleCopyCommand(installCommand, 'quick')}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: '#ffffff',
                    padding: '0.5rem',
                    borderRadius: 'var(--theme-border-radius-small)',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                  title={copiedCommand === 'quick' ? 'Copied!' : 'Copy command'}
                >
                  {copiedCommand === 'quick' ? 'Copied' : 'Copy'}
                </button>
              </Box>
              <p style={{
                marginTop: '1rem',
                color: 'var(--theme-text-secondary)',
                fontSize: '0.875rem'
              }}>
                Requires: <strong>Node.js 18+</strong>, <strong>npm 8+</strong>
              </p>
            </Box>
          )}
        </Box>

        <hr style={{ border: 'none', borderTop: '1px solid var(--theme-border-main)', margin: '3rem 0' }} />

        {/* Checksums */}
        <Box style={{ marginBottom: '3rem' }}>
          <h2 style={{ color: 'var(--theme-text-primary)', marginBottom: '1rem' }}>
            Verify Downloads
          </h2>
          <p style={{ color: 'var(--theme-text-secondary)', marginBottom: '1rem' }}>
            SHA256 checksums are available for all packages:
          </p>
          <a
            href={`https://qwickforge.com/downloads/v${latestVersion}/SHA256SUMS`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '0.5rem 1rem',
              border: '1px solid var(--theme-border-main)',
              borderRadius: 'var(--theme-border-radius-small)',
              color: 'var(--theme-primary)',
              textDecoration: 'none',
              marginBottom: '1rem',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-surface-variant)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            View SHA256SUMS
          </a>
          <Box style={{
            padding: '1rem',
            backgroundColor: 'var(--theme-surface-variant)',
            border: '1px solid var(--theme-border-main)',
            borderRadius: 'var(--theme-border-radius)'
          }}>
            <pre style={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              margin: 0,
              color: 'var(--theme-text-primary)'
            }}>
              {`# Verify downloaded file:\nsha256sum -c SHA256SUMS --ignore-missing`}
            </pre>
          </Box>
        </Box>

        {/* System Requirements */}
        <Box>
          <h2 style={{ color: 'var(--theme-text-primary)', marginBottom: '1.5rem' }}>
            System Requirements
          </h2>
          <Box style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            <Box style={{
              padding: '1.5rem',
              border: '1px solid var(--theme-border-main)',
              borderRadius: 'var(--theme-border-radius)',
              backgroundColor: 'var(--theme-surface)'
            }}>
              <h3 style={{ color: 'var(--theme-text-primary)', marginBottom: '1rem' }}>
                Minimum Requirements
              </h3>
              <ul style={{ color: 'var(--theme-text-secondary)', fontSize: '0.875rem', lineHeight: '1.8' }}>
                <li>Node.js 18.0.0 or higher</li>
                <li>npm 8.0.0 or higher</li>
                <li>Windows 10+, macOS 10.15+, or Linux (Ubuntu 20.04+)</li>
                <li>2GB RAM minimum</li>
                <li>500MB disk space</li>
              </ul>
            </Box>
            <Box style={{
              padding: '1.5rem',
              border: '1px solid var(--theme-border-main)',
              borderRadius: 'var(--theme-border-radius)',
              backgroundColor: 'var(--theme-surface)'
            }}>
              <h3 style={{ color: 'var(--theme-text-primary)', marginBottom: '1rem' }}>
                Need Help?
              </h3>
              <Box style={{ color: 'var(--theme-text-secondary)', fontSize: '0.875rem', lineHeight: '1.8' }}>
                <p style={{ marginBottom: '0.75rem' }}>
                  Check our <a href="https://qwickforge.com/support" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--theme-link-color)', textDecoration: 'none' }}>documentation</a> for installation guides and troubleshooting.
                </p>
                <p>
                  Join our community or <a href="https://www.linkedin.com/company/qwickapps/services/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--theme-link-color)', textDecoration: 'none' }}>contact support</a> for assistance.
                </p>
              </Box>
            </Box>
          </Box>
        </Box>
      </Section>
    </Box>
  );
};

export default DownloadsPage;
